// ====================================
// IMPORTS & STYLES
// ====================================
// External libraries, icons, and stylesheet
import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  Trophy,
  BookOpen,
  AlertCircle,
  RefreshCw,
  FileText,
  Target,
  Award,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Printer,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/mcq10Game.css";
import LoadingOverlay from "../components/LoadingOverlay";
import { API_BASE, DEV_MODE } from "../config";
import gameConfig from "../data/gameConfig.json";
import { calcTimeBonusPct } from "../utils/scoring";
import apiFetch from "../apiFetch";
import { reportHomeworkCompletion } from "../utils/homeworkCompletion";
import { reportDailyTaskCompletion } from "../utils/dailyTaskCompletion";
import { navigateReturn } from "../utils/navigation";

const MODE_CFG = gameConfig.mcq;

const MCQ_CACHE_KEY = (tier, topic) => `mcq_cache_tier_${tier}_topic_${topic}`;

const getCachedGameData = (tier, topic) => {
  try {
    const raw = localStorage.getItem(MCQ_CACHE_KEY(tier, topic));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedGameData = (tier, topic, data) => {
  try {
    localStorage.setItem(MCQ_CACHE_KEY(tier, topic), JSON.stringify(data));
  } catch {}
};

const clearCachedGameData = (tier, topic) => {
  localStorage.removeItem(MCQ_CACHE_KEY(tier, topic));
};

// ====================================
// MAIN GAME COMPONENT
// ====================================
const MCQGame = () => {
  const navigate = useNavigate();
  const goBack = () => navigateReturn(navigate);
  const location = useLocation();
  const selectedTier = location.state?.tier || "1";
  const selectedTierName = location.state?.tierName || "Novice";
  const selectedTopicCategory = location.state?.topicCategory || "Adventure";
  useEffect(() => { document.title = "MCQ Master — Owllet Reading Quest"; }, []);

  // ====================================
  // STATE MANAGEMENT
  // ====================================
  const [gameState, setGameState] = useState("loading");
  const [passage, setPassage] = useState("");
  const [questions, setQuestions] = useState([]);
  const [gameFilename, setGameFilename] = useState(null);
  const [gameLevel, setGameLevel] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(MODE_CFG.time);
  const [score, setScore] = useState(0);
  const [gameStats, setGameStats] = useState({
    totalQuestions: 10,
    correctAnswers: 0,
    incorrectAnswers: 0,
    timeBonus: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reviewMode, setReviewMode] = useState(false);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(0);
  const [testMode, setTestMode] = useState(false);
  const [showTestModeModal, setShowTestModeModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const testModalRef = useRef(null);
  const submitModalRef = useRef(null);

  useEffect(() => {
    if (!showTestModeModal) return;
    testModalRef.current?.querySelector("button")?.focus();
    const handler = (e) => { if (e.key === "Escape") setShowTestModeModal(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showTestModeModal]);

  useEffect(() => {
    if (!showSubmitModal) return;
    submitModalRef.current?.querySelector("button")?.focus();
    const handler = (e) => { if (e.key === "Escape") setShowSubmitModal(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showSubmitModal]);

  // ====================================
  // GAME CONFIGURATION
  // ====================================
  const API_URL = `${API_BASE}/api/generate-mcq`;
  const TOTAL_QUESTIONS = 10;
  const INITIAL_TIME = MODE_CFG.time;

  const fallbackGameData = {
    passage:
      "The Amazon rainforest, often called the 'lungs of the Earth,' is the world's largest tropical rainforest...",
    questions: [
      {
        question: "What is the Amazon rainforest commonly called?",
        options: {
          A: "The lungs of the Earth",
          B: "The heart of South America",
          C: "The green paradise",
        },
        correct: "A",
      },
      {
        question: "How many countries does the Amazon rainforest cover?",
        options: {
          A: "Seven countries",
          B: "Nine countries",
          C: "Eleven countries",
        },
        correct: "B",
      },
      // ... (rest of fallback questions)
    ],
  };

  // ====================================
  // SCORING & COIN CALCULATION
  // ====================================
  const calculateScoreAndCoins = (stats, timeRemaining) => {
    const accuracy = stats.correctAnswers / MODE_CFG.totalQuestions;
    const bonusPct = calcTimeBonusPct(accuracy, timeRemaining, MODE_CFG.time, MODE_CFG.maxTimeBonusPct);

    const baseScore = Math.round(accuracy * MODE_CFG.maxScore);
    const timeBonus = Math.round(baseScore * bonusPct);
    const finalScore = Math.min(MODE_CFG.maxScore, baseScore + timeBonus);
    const coinsEarned = Math.floor(finalScore * MODE_CFG.coinsMultiplier);

    return { finalScore, coinsEarned, timeBonus };
  };

  // ====================================
  // COIN PERSISTENCE
  // ====================================
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const persistCoins = async (earned) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || earned <= 0) return;

    try {
      const response = await apiFetch(`/api/resources/update-coins`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ userId, delta: earned }),
      });
      if (!response.ok) {
        console.error("Failed to update coins:", await response.text());
        return;
      }

      const xpRes = await apiFetch(`/api/resources/xp/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ userId, delta: earned * MODE_CFG.xpMultiplier }),
      });
      if (!xpRes.ok) {
        console.error("Failed to update XP:", await xpRes.text());
      }
    } catch (error) {
      console.error("Error sending coins/xp to backend:", error);
    }
  };

  //==========================
  // Statistic game saving data
  //==========================

  // add next to your other config constants
  const MODE_KEY = "mcq";

  const hasPostedRef = React.useRef(false);
  const hasInitRef = React.useRef(false);

  const saveNonRankedSession = async (finalScore, tier) => {
    if (hasPostedRef.current) return;
    hasPostedRef.current = true;

    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || Number.isNaN(userId)) return;

    try {
      const res = await apiFetch(`/api/sessions/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, mode: MODE_KEY, score: finalScore, tier: tier != null ? parseInt(tier, 10) : undefined }),
        keepalive: true,
      });

      if (!res.ok) {
        console.error("Session save failed:", res.status, await res.text());
      }
    } catch (e) {
      console.error("Network error saving session:", e);
    }
  };

  // ====================================
  // GAME LIFECYCLE
  // ====================================
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeGame();
  }, []);

  useEffect(() => {
    if (DEV_MODE) return;
    if (gameState === "playing" && timeLeft > 0 && !testMode) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === "playing" && !testMode) {
      finishGame();
    }
  }, [timeLeft, gameState, testMode]);

  const fetchGameData = async () => {
    setLoading(true);
    setError("");
    try {
      const cached = getCachedGameData(selectedTier, selectedTopicCategory);
      if (cached && cached.level === parseInt(selectedTier, 10)) return cached;

      const userId = parseInt(localStorage.getItem("userId"), 10);
      const response = await apiFetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, tier: selectedTier, tierName: selectedTierName, topicCategory: selectedTopicCategory }),
      });
      if (!response.ok) throw new Error(`Status: ${response.status}`);
      const data = await response.json();
      if (
        !data.passage ||
        !Array.isArray(data.questions) ||
        data.questions.length !== TOTAL_QUESTIONS
      ) {
        throw new Error("Invalid data");
      }
      setCachedGameData(selectedTier, selectedTopicCategory, data);
      return data;
    } catch (e) {
      console.error(e);
      navigate("/error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const initializeGame = async () => {
    hasPostedRef.current = false; // reset duplicate-post guard
    setTestMode(false);
    setGameState("loading");
    const data = await fetchGameData();
    if (!data) return;
    setGameFilename(data.filename || null);
    setGameLevel(data.level ?? null);
    setPassage(data.passage);
    setQuestions(data.questions);
    setGameState("playing");
    setCurrentQuestion(0);
    setUserAnswers({});
    setScore(0);
    setTimeLeft(INITIAL_TIME);
    setGameStats({
      totalQuestions: TOTAL_QUESTIONS,
      correctAnswers: 0,
      incorrectAnswers: 0,
      timeBonus: 0,
    });
    if (error) setTimeout(() => setError(""), 3000);
  };

  // ====================================
  // GAME CONTROL
  // ====================================
  const handleAnswerSelect = (index, choice) => {
    setUserAnswers((prev) => ({ ...prev, [index]: choice }));
  };

  const nextQuestion = () =>
    setCurrentQuestion((i) => Math.min(i + 1, questions.length - 1));
  const previousQuestion = () => setCurrentQuestion((i) => Math.max(i - 1, 0));

  const finishGame = async (isExplicit = false) => {

    let correctCount = 0;
    let incorrectCount = 0;

    questions.forEach((q, i) => {
      if (userAnswers[i] === q.correct) correctCount++;
      else if (userAnswers[i]) incorrectCount++;
    });

    const {
      finalScore,
      timeBonus,
      coinsEarned: coins,
    } = calculateScoreAndCoins(
      {
        correctAnswers: correctCount,
        incorrectAnswers: incorrectCount,
        totalQuestions: questions.length,
      },
      timeLeft,
    );

    setScore(finalScore);
    setGameStats({
      totalQuestions: questions.length,
      correctAnswers: correctCount,
      incorrectAnswers: incorrectCount,
      timeBonus,
    });

    clearCachedGameData(selectedTier, selectedTopicCategory);
    if (isExplicit && !testMode) {
      await persistCoins(coins);
      await saveNonRankedSession(finalScore, selectedTier);
    }

    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (userId && gameFilename && gameLevel != null) {
      apiFetch(`/api/mark-mcq-played`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, filename: gameFilename, level: gameLevel }),
      }).catch(() => {});
    }

    if (userId) {
      apiFetch(`/api/results/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          gameType: "mcq",
          title: `${selectedTopicCategory} — Tier ${selectedTierName}`,
          score: correctCount,
          maxScore: questions.length,
          resultJson: { passage, questions, userAnswers, correctAnswers: correctCount, timeBonus },
        }),
      }).then(async (r) => {
        if (r.ok) {
          const d = await r.json();
          reportHomeworkCompletion(d.id ?? null, "game");
          reportDailyTaskCompletion();
        }
      }).catch(() => {});
    }

    setTimeout(() => {
      setGameState("results");
    }, 0);
  };

  // ====================================
  // UTILS
  // ====================================
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${String(secs).padStart(2, "0")}`;
  };

  const answeredCount = () => Object.keys(userAnswers).length;

  // ====================================
  // RENDER
  // ====================================
  if (gameState === "loading") return <LoadingOverlay message={loading ? "Generating questions…" : "Preparing quiz…"} />;

  if (gameState === "results") {
    if (reviewMode) {
      const question = questions[currentReviewQuestion];
      const userAnswer = userAnswers[currentReviewQuestion];
      const isCorrect = userAnswer === question.correct;

      return (
        <div className="review-screen">
          <div className="review-header">
            <h2>Answer Review</h2>
            <button
              className="button secondary"
              onClick={() => setReviewMode(false)}
            >
              <ArrowLeft size={16} /> Back
            </button>
          </div>

          <div>
            <div className="question-review">
              <div className="review-navigation">
                {questions.map((q, i) => {
                  const ua = userAnswers[i];
                  const answered = ua !== undefined;
                  const correct = ua === q.correct;
                  return (
                    <button
                      key={i}
                      className={`nav-btn ${i === currentReviewQuestion ? "current" : ""} ${answered ? (correct ? "correct" : "incorrect") : ""}`}
                      onClick={() => setCurrentReviewQuestion(i)}
                    >
                      {i + 1}
                    </button>
                  );
                })}
                <span className="review-score-circle" style={{ marginLeft: "auto" }}>
                  {gameStats.correctAnswers}/{questions.length}
                </span>
                <button className="review-action-btn" onClick={() => window.print()} title="Print / Save as PDF">
                  <Printer size={15} />
                </button>
              </div>

              <div
                className={`question-review-block ${isCorrect ? "correct" : "incorrect"}`}
              >
                <h4 className="question-title">
                  Question {currentReviewQuestion + 1}
                </h4>

                <p className="review-question-text">{question.question}</p>

                <div className="review-answer-section">
                  <div className="your-answer">
                    <strong>Your answer:</strong>{" "}
                    {userAnswer ? (
                      <>
                        <span>
                          {userAnswer}) {question.options[userAnswer]}
                        </span>
                        {isCorrect ? (
                          <span className="answer-icon correct-icon">
                            ✅ Correct
                          </span>
                        ) : (
                          <span className="answer-icon incorrect-icon">
                            ❌ Incorrect
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="no-answer">No answer provided</span>
                    )}
                  </div>

                  {!isCorrect && (
                    <div className="correct-answer">
                      <strong>Correct answer:</strong>{" "}
                      <span>
                        {question.correct}) {question.options[question.correct]}
                      </span>
                    </div>
                  )}
                </div>

                {question.explanation && (
                  <div className="review-explanation">
                    <h4>Explanation</h4>
                    <p>{question.explanation}</p>
                  </div>
                )}

                <div className="review-passage">
                  <h4>Reading Passage</h4>
                  <p className="passage-text">{passage}</p>
                </div>
              </div>

              <div className="review-controls">
                <button
                  className="button ghost"
                  onClick={() =>
                    setCurrentReviewQuestion((q) => Math.max(0, q - 1))
                  }
                  disabled={currentReviewQuestion === 0}
                >
                  <ArrowLeft size={16} />{" "}
                  Previous
                </button>
                <button
                  className="button ghost"
                  onClick={() =>
                    setCurrentReviewQuestion((q) =>
                      Math.min(questions.length - 1, q + 1),
                    )
                  }
                  disabled={currentReviewQuestion === questions.length - 1}
                >
                  Next{" "}
                  <ArrowLeft
                    size={16}
                    style={{ transform: "rotate(180deg)" }}
                  />
                </button>
              </div>
            </div>
          </div>

        <div className="print-only">
          <div className="print-title">MCQ Quiz Review</div>
          <p className="print-score">{gameStats.correctAnswers} / {questions.length} correct</p>
          <h2>Reading Passage</h2>
          <div className="print-passage">{passage}</div>
          <h2>Questions &amp; Answers</h2>
          {questions.map((q, i) => {
            const ua = userAnswers[i];
            const isCorrect = ua === q.correct;
            return (
              <div key={i} className="print-question">
                <div className="print-q-header">
                  <span className="print-q-num">Q{i + 1}</span>
                  <span className={isCorrect ? "print-correct-badge" : "print-wrong-badge"}>
                    {isCorrect ? "Correct ✓" : "Incorrect ✗"}
                  </span>
                </div>
                <p className="print-q-text">{q.question}</p>
                {Object.entries(q.options || {}).map(([key, text]) => {
                  const isOpt = key === q.correct;
                  const isSelected = ua === key;
                  return (
                    <div
                      key={key}
                      className={`print-option${isOpt ? " print-correct" : ""}${isSelected && !isOpt ? " print-wrong" : ""}`}
                    >
                      <strong>{key}:</strong> {text}
                      {isOpt && " ✓"}
                      {isSelected && !isOpt && " ✗ (your answer)"}
                    </div>
                  );
                })}
                {q.explanation && (
                  <div className="print-explanation">
                    <div className="print-label">Explanation</div>
                    <p>{q.explanation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      );
    }

    return (
      <div className="results-screen">
        <div className="results-content">
          <div className="results-header">
            <Trophy size={64} className="trophy-icon" />
            <h2>Quiz Complete!</h2>
          </div>

          <div className="stats-grid">
            <StatCard icon={<Target />} label="Score" value={score} />
            <StatCard
              icon={<CheckCircle />}
              label="Correct"
              value={gameStats.correctAnswers}
            />
            <StatCard
              icon={<XCircle />}
              label="Incorrect"
              value={gameStats.incorrectAnswers}
            />
            <StatCard
              icon={<Clock />}
              label="Time Bonus"
              value={`+${gameStats.timeBonus}`}
            />
          </div>

          <div className="actions">
            <button
              className="primary-button"
              onClick={() => setReviewMode(true)}
            >
              <FileText size={20} /> Review Answers
            </button>
            <button className="secondary-button" onClick={initializeGame}>
              <RefreshCw size={20} /> New Quiz
            </button>
            <button
              className="tertiary-button"
              onClick={goBack}
            >
              <ArrowLeft size={20} /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ====================================
  // MAIN GAME INTERFACE
  // ====================================
  return (
    <div className="mcq-game">
      <header className="game-header">
        <div className="header-left">
          <FileText size={24} />
          <h1>MCQ Challenge</h1>
        </div>
        <div className="header-right">
          <div className="header-stats">
            <div className={`timer ${!testMode && timeLeft < 60 ? "urgent" : ""}`}>
              <Clock /> {testMode ? "∞" : formatTime(timeLeft)}
            </div>
            <div className="score">
              <Star /> {score}
            </div>
            {testMode
              ? <span className="test-mode-badge"><Clock size={11} /> Learning Mode</span>
              : <button className="test-mode-btn" onClick={() => setShowTestModeModal(true)}><Clock size={13} /> Learning Mode</button>
            }
          </div>
          <button className="header-back-btn" onClick={goBack}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </header>

      {showTestModeModal && (
        <div className="test-mode-modal-overlay" onClick={() => setShowTestModeModal(false)}>
          <div
            className="test-mode-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mcq-testmode-title"
            ref={testModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="test-mode-modal-icon" aria-hidden="true"><Clock size={26} /></div>
            <h3 id="mcq-testmode-title">Enable Learning Mode?</h3>
            <p>The timer will stop and you'll have unlimited time to finish, but <strong>no coins or XP</strong> will be awarded. Your attempt will still be saved to your history.</p>
            <div className="test-mode-modal-actions">
              <button className="test-mode-confirm-btn" onClick={() => { setTestMode(true); setShowTestModeModal(false); }}>
                Confirm — Enable Learning Mode
              </button>
              <button className="test-mode-cancel-btn" onClick={() => setShowTestModeModal(false)}>
                Cancel — Keep Playing Normally
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Two-column game layout */}
      <div className="game-content">
        {/* LEFT: Passage */}
        <div className="passage-section">
          <h3>Reading Passage</h3>
          <p className="passage-text">{passage}</p>
        </div>

        {/* RIGHT: Questions */}
        <div className="questions-section">
          {/* Question number nav */}
          <div className="question-navigation">
            <div className="question-nav">
              {questions.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentQuestion(idx)}
                  className={`nav-btn ${idx === currentQuestion ? "current" : ""} ${userAnswers[idx] ? "answered" : ""}`}
                  title={`Question ${idx + 1}`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
            <span className="progress-info">
              {answeredCount()}/{questions.length} answered
            </span>
          </div>

          {/* Active question */}
          <div className="question-panel">
            <div className="question-header">
              <h4>Question {currentQuestion + 1} of {questions.length}</h4>
            </div>
            <p className="question-text">
              {questions[currentQuestion].question}
            </p>
            <div className="options-list">
              {Object.entries(questions[currentQuestion].options).map(([key, text]) => (
                <label key={key} className="option-item">
                  <input
                    type="radio"
                    name={`q-${currentQuestion}`}
                    checked={userAnswers[currentQuestion] === key}
                    onChange={() => handleAnswerSelect(currentQuestion, key)}
                  />
                  <span className="option-key">{key})</span>
                  <span className="option-text">{text}</span>
                </label>
              ))}
            </div>

            {/* Nav / finish buttons immediately below options */}
            <div className="control-buttons">
              <button
                onClick={previousQuestion}
                disabled={currentQuestion === 0}
                className="nav-button"
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {currentQuestion === questions.length - 1 ? (
                <button
                  onClick={() => {
                    const allAnswered = Object.keys(userAnswers).length === questions.length;
                    if (allAnswered || testMode || timeLeft <= 0) {
                      finishGame(true);
                    } else {
                      setShowSubmitModal(true);
                    }
                  }}
                  className="submit-button"
                >
                  <Award size={16} /> Complete Quiz
                </button>
              ) : (
                <button onClick={nextQuestion} className="nav-button">
                  Next <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showSubmitModal && (
        <div className="submit-confirm-overlay" onClick={() => setShowSubmitModal(false)}>
          <div
            className="submit-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mcq-submit-title"
            ref={submitModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="submit-confirm-icon" aria-hidden="true">
              <AlertTriangle size={26} />
            </div>
            <h3 id="mcq-submit-title">Unanswered Questions</h3>
            <p>
              You have{" "}
              {questions.length - Object.keys(userAnswers).length}{" "}
              unanswered question(s). Are you sure you want to submit?
            </p>
            <div className="submit-confirm-actions">
              <button className="submit-confirm-back" onClick={() => setShowSubmitModal(false)}>
                Go Back
              </button>
              <button className="submit-confirm-go" onClick={() => { setShowSubmitModal(false); finishGame(true); }}>
                Submit Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ====================================
// SUB-COMPONENTS
// ====================================
const StatCard = ({ icon, label, value }) => (
  <div className="stat-card">
    <div className="stat-icon">{icon}</div>
    <div className="stat-info">
      <p className="stat-label">{label}</p>
      <p className="stat-value">{value}</p>
    </div>
  </div>
);

export default MCQGame;
